import useAuth from 'hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col } from 'react-bootstrap';
import baseController from 'api/base.controller';
import HomeEvento from '../dashboards/graficos/homeEventos';
import FullScreenLoading from '../sistema/fullScreenLoading';
import React, { useEffect, useState, useCallback, useMemo } from 'react';

export default function Home() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [eventosAtivosData, setEventosAtivosData] = useState([]);
  const [ultimosEventosData, setUltimosEventosData] = useState([]);
  const [lojasClienteMensal, setLojasClienteMensal] = useState([]);
  const [eventosAtivosByLojaData, setEventosAtivosByLojaData] = useState([]);
  const [ultimosEventosByLojaData, setUltimosEventosByLojaData] = useState([]);

  const isLojaEvento = useMemo(() => 
    ['LE-A', 'LE-V'].includes(auth.role ?? JSON.parse(localStorage.getItem('auth')).role), 
    [auth.role]
  );
  
  const isClienteMensal = useMemo(() => 
    ['CM-A', 'CM-V'].includes(auth.role ?? JSON.parse(localStorage.getItem('auth')).role), 
    [auth.role]
  );

  const headers = useMemo(() => ({
    Authorization: auth.token ?? JSON.parse(localStorage.getItem('auth')).token
  }), [auth.token]);

  const transformLojasClienteMensal = useCallback((data) => {
    return data?.map(loja => ({
      id: loja.eventoId,
      nome: loja.lojaNome,
      lojaId: loja.lojaId
    })) || [];
  }, []);

  const transformEventosByLoja = useCallback((data) => {
    const transformed = [];
    data?.forEach(loja => {
      loja.eventos?.forEach(evento => {
        transformed.push({
          id: evento.id,
          nome: evento.nome,
          local: evento.local,
          lojaId: loja.lojaId,
          lojaNome: loja.lojaNome,
          dataInicio: evento.dataInicio,
          dataTermino: evento.dataTermino
        });
      });
    });
    return transformed;
  }, []);

  const handleNavigate = useCallback((value) => {
    navigate(`/evento/dashboard/${value.id}/${value.lojaId}`);
  }, [navigate]);

  const singleLoja = useMemo(() => {
    if (isClienteMensal && lojasClienteMensal.length === 1) {
      return lojasClienteMensal[0];
    }
    return null;
  }, [isClienteMensal, lojasClienteMensal]);

  const loadData = useCallback(async () => {
    setIsLoading(true);

    try {
      if (isClienteMensal) {
        const resLojasClienteMensal = await baseController.get('Home/LojasClienteMensal', { headers });
        setLojasClienteMensal(transformLojasClienteMensal(resLojasClienteMensal.data));
      } else if (isLojaEvento) {
        const [resEventosAtivosByLoja, resUltimosEventosByLoja] = await Promise.all([
          baseController.get('Home/EventosAtivosByLoja', { headers }),
          baseController.get('Home/UltimosEventosByLoja', { headers })
        ]);

        setEventosAtivosByLojaData(transformEventosByLoja(resEventosAtivosByLoja.data));
        setUltimosEventosByLojaData(transformEventosByLoja(resUltimosEventosByLoja.data));
      } else {
        const [eventosAtivos, ultimosEventos] = await Promise.all([
          baseController.get('Home/EventosAtivos', { headers }),
          baseController.get('Home/UltimosEventos', { headers })
        ]);

        setEventosAtivosData(eventosAtivos.data);
        setUltimosEventosData(ultimosEventos.data);
      }
    } catch (error) {
      console.error('Error loading home data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isClienteMensal, isLojaEvento, headers, transformLojasClienteMensal, transformEventosByLoja]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (isLoading) {
    return <FullScreenLoading />;
  }

  if (singleLoja) {
    handleNavigate(singleLoja);
    return null;
  }

  if (isClienteMensal) {
    return (
      <Card className='dashboard-card home-card-container'>
        <div className='home-eventos-container'>
          <h3 className='mt-1 home-title'>Lojas em Operação</h3>
          <Row className='g-1 mb-1 row-eventos'>
            <Col md={12} className='col-eventos'>
              <HomeEvento 
                isClienteMensal
                data={lojasClienteMensal}
              />
            </Col>
          </Row>
        </div>
      </Card>
    );
  }

  if (isLojaEvento) {
    return (
      <Card className='dashboard-card home-card-container'>
        <div className='home-eventos-container'>
          <h3 className='mt-1 home-title'>Eventos em Operação</h3>
          <Row className='g-1 mb-1 row-eventos'>
            <Col md={12} className='col-eventos'>
              <HomeEvento 
                isLojaEvento
                data={eventosAtivosByLojaData}
              />
            </Col>
          </Row>
        </div>
        <div className='home-eventos-container'>
          <h3 className='mt-1 home-title'>Eventos Realizados</h3>
          <Row className='g-1 mb-1 row-eventos'>
            <Col md={12} className='col-eventos'>
              <HomeEvento 
                isLojaEvento
                data={ultimosEventosByLojaData}
              />
            </Col>
          </Row>
        </div>
      </Card>
    );
  }

  return (
    <Card className='dashboard-card home-card-container'>
      <div className='home-eventos-container'>
        <h3 className='mt-1 home-title'>Eventos em Operação</h3>
        <Row className='g-1 mb-1 row-eventos'>
          <Col md={12} className='col-eventos'>
            <HomeEvento 
              data={eventosAtivosData}
              isClienteMensal={isClienteMensal}
            />
          </Col>
        </Row>
      </div>
      <div className='home-eventos-container'>
        <h3 className='mt-1 home-title'>Eventos Realizados</h3>
        <Row className='g-1 mb-1 row-eventos'>
          <Col md={12} className='col-eventos'>
            <HomeEvento data={ultimosEventosData}/>
          </Col>
        </Row>
      </div>
    </Card>
  );
}